import { hasPermission } from "./permission.service";

/**
 * Subject-level data scoping helpers.
 *
 * Policy (confirmed with product):
 *  - localStorage.userSubjects = []            -> unrestricted (admin-like)
 *  - "Data Scope / All Subjects" permission    -> unrestricted (explicit bypass)
 *  - otherwise                                 -> restricted to listed subjects
 */

export const getUserSubjects = (): number[] => {
  try {
    const raw = localStorage.getItem("userSubjects");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  } catch {
    return [];
  }
};

export const hasAllSubjectsBypass = (): boolean => {
  return hasPermission("Data Scope", "All Subjects");
};

export const isSubjectScoped = (): boolean => {
  if (hasAllSubjectsBypass()) return false;
  return getUserSubjects().length > 0;
};

/**
 * True if the user is allowed to see data tagged to the given subject.
 * Items with a null/undefined subject are not hidden (e.g. taxonomy-less rows).
 */
export const canSeeSubject = (
  subjectId: number | string | null | undefined
): boolean => {
  if (!isSubjectScoped()) return true;
  if (subjectId == null || subjectId === "") return true;
  return getUserSubjects().includes(Number(subjectId));
};

/**
 * Filter an array of items by subject scope.
 *
 *   filterBySubjectScope(items)                       // looks at subject_id / subjectId
 *   filterBySubjectScope(items, it => it.dbSubjectId) // custom extractor
 *
 * Items with a null/undefined subject id are kept (not hidden).
 */
export const filterBySubjectScope = <T extends Record<string, any>>(
  items: T[],
  extract?: (item: T) => number | string | null | undefined
): T[] => {
  if (!isSubjectScoped()) return items;
  const allowed = new Set(getUserSubjects());
  const pick = extract || ((it: T) => it.subject_id ?? it.subjectId);
  return items.filter((it) => {
    const sid = pick(it);
    if (sid == null || sid === "") return true;
    return allowed.has(Number(sid));
  });
};

/**
 * Convenience — returns both the list and whether scoping is active.
 */
export const getSubjectScope = (): { subjectIds: number[]; isScoped: boolean } => {
  return {
    subjectIds: getUserSubjects(),
    isScoped: isSubjectScoped(),
  };
};

/**
 * Set the user's allowed subject ids in localStorage. Called once at login
 * after resolving user_subjects for the current user.
 */
export const setUserSubjects = (subjectIds: number[]): void => {
  try {
    localStorage.setItem("userSubjects", JSON.stringify(subjectIds || []));
  } catch {
    /* ignore quota errors */
  }
};
